import { Alert, Button, Drawer, Form, Grid, Input, Select, Space } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, errorLines } from "../app/problems.ts";

type Subject = components["schemas"]["SubjectDetails"];
type RateGroup = components["schemas"]["SubjectRateGroup"];

export const rateGroups: RateGroup[] = ["Core", "English", "Other"];

interface SubjectForm {
  name: string;
  rateGroup: RateGroup;
}

interface Props {
  open: boolean;
  subject?: Subject | undefined;
  onClose(): void;
  onSaved(subject: Subject, created: boolean): void;
}

export function SubjectDrawer({ open, subject, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SubjectForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: SubjectForm) => {
      const body = { name: values.name.trim(), rateGroup: values.rateGroup };
      const result = subject
        ? await api.PUT("/academic/subjects/{id}", { params: { path: { id: subject.id } }, body })
        : await api.POST("/academic/subjects", { body });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["academic"] });
      setServerErrors([]);
      onSaved(saved, !subject);
    },
    onError: (err) => setServerErrors(errorLines(err, t("app.error"))),
  });

  return (
    <Drawer
      title={subject ? t("subjects.edit") : t("subjects.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 440 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<SubjectForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={subject ? { name: subject.name, rateGroup: subject.rateGroup } : { rateGroup: "Other" }}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item name="name" label={t("subjects.name")} rules={[{ required: true, message: t("common.required") }]}>
          <Input maxLength={200} placeholder={t("subjects.nameHint")} />
        </Form.Item>
        <Form.Item name="rateGroup" label={t("subjects.rateGroup")} extra={t("subjects.rateGroupHint")}>
          <Select options={rateGroups.map((g) => ({ value: g, label: t(`subjects.rateGroups.${g}`) }))} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
